import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["as", "elementRef", "overflow", "variant", "padding", "children"];
var _dec, _dec2, _class, _ModalBody;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { View } from '@instructure/ui-view';
import { testable } from '@instructure/ui-testable';
import { omitProps } from '@instructure/ui-react-utils';
import { getComputedStyle } from '@instructure/ui-dom-utils';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
parent: Modal
id: Modal.Body
---
**/
let ModalBody = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_ModalBody = class ModalBody extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);

    // We detect if -moz- prefixed style is present to identify whether we are in Firefox browser
    const style = this.ref && getComputedStyle(this.ref);
    const isFirefox = !!(style && Array.prototype.slice.call(style).join('').match(/(?:-moz-)/));
    if (isFirefox) {
      this.setState({
        isFirefox
      });
    }
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  getFinalRef(el) {
    if (!el) {
      return void 0;
    }
    if (el instanceof Element) {
      return el;
    }
    if (el.ref) {
      return this.getFinalRef(el.ref);
    }
    return void 0;
  }
  render() {
    var _this$props$styles, _finalRef$scrollHeigh, _finalRef$getBounding, _finalRef$getBounding2;
    const _this$props3 = this.props,
      as = _this$props3.as,
      elementRef = _this$props3.elementRef,
      overflow = _this$props3.overflow,
      variant = _this$props3.variant,
      padding = _this$props3.padding,
      children = _this$props3.children,
      rest = _objectWithoutProperties(_this$props3, _excluded);
    const passthroughProps = View.omitViewProps(omitProps(rest, ModalBody.allowedProps), ModalBody);
    const isFit = overflow === 'fit';
    // this recursive function is needed because `ref` can be a React component.
    // TODO rethink, the 'as' prop, likely its not a good idea to allow React
    // components. See INSTUI-4674
    const finalRef = this.getFinalRef(this.ref);
    return _jsx(View, {
      ...passthroughProps,
      display: "block",
      width: isFit ? '100%' : void 0,
      height: isFit ? '100%' : void 0,
      elementRef: this.handleRef,
      as: as,
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.modalBody,
      padding: padding
      // check if there is a scrollbar, if so, the element has to be tabbable to be able to scroll with keyboard only
      // epsilon tolerance is used to avoid false positives, this is generally safer than Math rounding techniques
      ,
      ...(finalRef && Math.abs(((_finalRef$scrollHeigh = finalRef.scrollHeight) !== null && _finalRef$scrollHeigh !== void 0 ? _finalRef$scrollHeigh : 0) - ((_finalRef$getBounding = (_finalRef$getBounding2 = finalRef.getBoundingClientRect()) === null || _finalRef$getBounding2 === void 0 ? void 0 : _finalRef$getBounding2.height) !== null && _finalRef$getBounding !== void 0 ? _finalRef$getBounding : 0)) > 0.05 ? {
        tabIndex: 0
      } : {}),
      children: children
    });
  }
}, _ModalBody.displayName = "ModalBody", _ModalBody.componentId = 'Modal.Body', _ModalBody.propTypes = propTypes, _ModalBody.allowedProps = allowedProps, _ModalBody.defaultProps = {
  padding: 'medium',
  as: 'div',
  variant: 'default'
}, _ModalBody)) || _class) || _class);
export default ModalBody;
export { ModalBody };